import { useCallback, useMemo } from "react";
import {
  useLocation,
  useNavigation,
  useSearchParams,
  type Location,
  type SetURLSearchParams,
} from "react-router";

/**
 * The location a URL write composes on, and a control that must show the
 * click before the read lands renders from: the navigation in flight when it
 * stays on this path, else the location on screen.
 *
 * While a same-route re-run is pending, React Router's transition holds the
 * previous screen, and `useLocation()` / `useSearchParams()` with it. A write
 * built from them drops the change still in flight: a tab click during a
 * filter's read would un-apply the filter.
 *
 * Needs a data router, as `useNavigation()` does; tests render under
 * `TestDataRouter` from `@shuttlerock/testing`.
 */
export function usePendingLocation(): Location {
  const location = useLocation();
  const { location: next } = useNavigation();
  return next?.pathname === location.pathname ? next : location;
}

/**
 * `useSearchParams()` on the URL in flight (`usePendingLocation()`): the
 * params are those of the navigation still loading, and a functional update
 * receives a copy of them rather than the params on screen.
 */
export function usePendingSearchParams(): [URLSearchParams, SetURLSearchParams] {
  const { search } = usePendingLocation();
  const [, setSearchParams] = useSearchParams();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const setPendingSearchParams = useCallback<SetURLSearchParams>(
    (next, options) =>
      setSearchParams(
        typeof next === "function" ? next(new URLSearchParams(search)) : next,
        options,
      ),
    [search, setSearchParams],
  );
  return [searchParams, setPendingSearchParams];
}
